import React from "react";
import { Tooltip, tooltipClasses, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  TriggerBox,
  ExplanationIcon,
  ExplanationLabel,
} from "./ExplanationTooltip.styled";

/** Tooltip with multiline support and a custom dark theme. */
const StyledTooltip = styled(
  ({ className, ...props }: React.ComponentProps<typeof Tooltip>) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ),
)(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 360,
    padding: "10px 14px",
    fontSize: "13px",
    fontFamily: "Nunito, sans-serif",
    fontWeight: 400,
    lineHeight: 1.6,
    // Preserves \n line breaks in multi-description explanation text
    whiteSpace: "pre-line",
    backgroundColor: "#1C2B4A",
    borderRadius: "8px",
    boxShadow: "0 4px 16px rgba(28, 43, 74, 0.18)",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#1C2B4A",
  },
}));

interface ExplanationTooltipProps {
  explanation: string;
}

/**
 * Shows an ⓘ "Explanation" label.
 * - Desktop (pointer): tooltip appears on hover.
 * - Mobile (touch):    tooltip appears immediately on tap and stays visible
 *                      for 3 s after the finger lifts (enterTouchDelay=0).
 * Returns null when there is no explanation text.
 */
const ExplanationTooltip: React.FC<ExplanationTooltipProps> = ({
  explanation,
}) => {
  const { t } = useTranslation();

  if (!explanation) return null;

  return (
    <StyledTooltip
      title={explanation}
      placement="top"
      arrow
      enterDelay={100}
      leaveDelay={100}
      // Touch-friendly: show on tap, not on long-press
      enterTouchDelay={0}
      leaveTouchDelay={3000}
    >
      <TriggerBox>
        <ExplanationIcon />
        <ExplanationLabel component="span">
          {t("common.explanation")}
        </ExplanationLabel>
      </TriggerBox>
    </StyledTooltip>
  );
};

export default ExplanationTooltip;
